import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { authApi, type AuthPublicConfig, type AuthUser } from '../api/auth';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import { billingApi, type PlanDefinition } from '../api/billing';
import LandingAtmosphere from '../components/ui/LandingAtmosphere';
import ParrotEmblem from '../components/ui/ParrotEmblem';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import { getLegalLinks } from '../config/legal';
import { getInstanceInfo, isDemoHost } from '../config/instance';
import { getTenantSubdomain } from '../config/api';
import { pathFromLoginNext } from '../utils/auth-return';
import {
  CONTRACTING_NOTES,
  DEMO_LANDING_HEADLINE,
  DEMO_LANDING_SUPPORT,
  HOW_BIRDMAN_WORKS,
  LANDING_HEADLINE,
  LANDING_SUPPORT,
  MARKETING_PLANS,
  SUBSCRIBE_STEPS,
  SUITE_MODULES,
  UPDATES_AND_IMPROVEMENTS,
  type MarketingPlan,
} from '../content/marketingSuite';

function formatLimit(
  value: number | null,
  unit: string,
  unlimitedLabel: string,
  prefix = 'Up to ',
): string {
  if (value === null) return unlimitedLabel;
  return `${prefix}${value.toLocaleString()} ${unit}`;
}

function plansFromApi(apiPlans: PlanDefinition[]): MarketingPlan[] {
  const byKey = Object.fromEntries(apiPlans.map((p) => [p.key, p]));
  return MARKETING_PLANS.map((fallback) => {
    const live = byKey[fallback.key];
    if (!live) return fallback;
    return {
      ...fallback,
      name: live.name,
      blurb: live.description || fallback.blurb,
      priceMonthlyUsd: live.priceMonthlyUsd,
      priceMonthlyZar: live.priceMonthlyZar,
      limits: {
        birds: formatLimit(live.limits.birds, 'birds', 'Unlimited birds'),
        users: formatLimit(live.limits.users, 'users', 'Unlimited users', ''),
        knowledgeSources: formatLimit(
          live.limits.knowledgeSources,
          'knowledge sources',
          'Unlimited knowledge sources',
        ),
        aiInvocations: formatLimit(
          live.limits.aiInvocationsPerMonth,
          'AI calls / month',
          'Unlimited AI calls',
        ),
        storage:
          live.limits.storageGb === null
            ? 'Unlimited storage'
            : `${live.limits.storageGb} GB photos & documents`,
        alwaysOn: live.limits.alwaysOnCompute
          ? 'Always-on compute allowed'
          : 'Autostop hosting (idle scale-down)',
      },
    };
  });
}

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function BackToTopButton() {
  return (
    <div className="mt-12 flex justify-end border-t border-teal-800/10 pt-6">
      <button
        type="button"
        onClick={() => scrollToId('top')}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-800 transition hover:text-teal-950"
      >
        <span aria-hidden>↑</span>
        Back to top
      </button>
    </div>
  );
}

export default function Login() {
  const [authMode, setAuthMode] = useState<'signin' | 'register'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState<AuthPublicConfig | null>(null);
  const [instanceWaking, setInstanceWaking] = useState(false);
  const [plans, setPlans] = useState<MarketingPlan[]>(MARKETING_PLANS);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const reducedMotion = usePrefersReducedMotion();
  const legalLinks = getLegalLinks();
  const instance = getInstanceInfo();
  const purchasedInstance = Boolean(getTenantSubdomain());

  const demoSelfRegister = purchasedInstance ? false : (authConfig?.demoSelfRegister ?? isDemoHost());
  const googleClientId =
    authConfig?.googleClientId ||
    (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() ||
    null;
  const googleEnabled = authConfig?.googleAuthEnabled ?? Boolean(googleClientId);

  const headline = demoSelfRegister ? DEMO_LANDING_HEADLINE : LANDING_HEADLINE;
  const supportCopy = demoSelfRegister ? DEMO_LANDING_SUPPORT : LANDING_SUPPORT;

  const openAuthPanel = (mode: 'signin' | 'register') => {
    setAuthMode(mode);
    setError('');
    scrollToId('sign-in');
  };

  useEffect(() => {
    if (demoSelfRegister) {
      setAuthMode('register');
    }
  }, [demoSelfRegister]);

  useEffect(() => {
    let cancelled = false;
    authApi
      .getConfig()
      .then((cfg) => {
        if (cancelled) return;
        setAuthConfig(cfg);
        setInstanceWaking(false);
      })
      .catch(() => {
        if (!cancelled && purchasedInstance) setInstanceWaking(true);
      });
    return () => {
      cancelled = true;
    };
  }, [purchasedInstance]);

  useEffect(() => {
    if (purchasedInstance) return;
    let cancelled = false;
    billingApi
      .getPlans()
      .then((live) => {
        if (!cancelled && Array.isArray(live) && live.length > 0) {
          setPlans(plansFromApi(live));
        }
      })
      .catch(() => {
        /* keep static catalog fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [purchasedInstance]);

  const completeAuth = (token: string, user: AuthUser) => {
    login(token, {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
    });
    navigate(pathFromLoginNext(searchParams.get('next')));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await authApi.login(email, password);
      completeAuth(result.token, result.user);
    } catch (err: any) {
      let errorMessage = 'Login failed. Please try again.';

      if (err?.status === 500) {
        errorMessage = purchasedInstance
          ? 'The instance hit a server error. Wait a moment and try again.'
          : 'Server error. Please check if the database is running.';
      } else if (err?.status === 401) {
        errorMessage = err?.data?.message || 'Invalid credentials. Please check your email and password.';
      } else if (err?.status === 400) {
        errorMessage = 'Please enter both email and password.';
      } else if (!err?.status && /timeout|abort|fetch|network|starting/i.test(err?.message || '')) {
        errorMessage = purchasedInstance
          ? 'Your instance is starting (it sleeps when idle). This can take up to a minute — try again.'
          : err.message;
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await authApi.register({
        email,
        password: regPassword,
        firstName: regFirstName.trim(),
        lastName: regLastName.trim(),
      });
      completeAuth(result.token, result.user);
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async (idToken: string) => {
    setError('');
    setIsLoading(true);
    try {
      const result = await authApi.google(idToken);
      completeAuth(result.token, result.user);
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const navLinks = [
    { id: 'suite', label: 'Suite' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'subscribe', label: 'Subscribe' },
    { id: 'updates', label: 'Updates' },
  ];

  const reveal = (delayClass: string) => (reducedMotion ? '' : delayClass);

  if (purchasedInstance) {
    return (
      <div className="marketing-landing relative min-h-screen">
        <LandingAtmosphere className="fixed inset-0" />
        <main className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-16 sm:px-6">
          <div className="mb-8 flex items-center gap-2.5">
            <ParrotEmblem className="h-9 w-9" animated={!reducedMotion} />
            <span className="marketing-brand-title text-2xl font-bold">BirdMan</span>
          </div>
          <div className="rounded-3xl border border-teal-900/10 bg-white/85 p-7 backdrop-blur-xl">
            <h1 className="marketing-display text-2xl font-semibold text-[var(--bm-landing-ink)]">
              Sign in
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {instance.name ? `${instance.name} — dedicated instance` : 'Your dedicated BirdMan instance'}
            </p>
            {instanceWaking && !error && (
              <p className="mt-3 text-sm text-slate-500">
                Your instance sleeps when idle and can take up to a minute to start.
              </p>
            )}
            {error && (
              <div
                role="alert"
                className="mt-4 rounded-xl border border-rose-200/90 bg-rose-50/90 px-3.5 py-3 text-sm text-rose-700"
              >
                {error}
              </div>
            )}
            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              <label className="block text-sm font-medium text-slate-700">
                Email
                <input
                  type="email"
                  autoComplete="email"
                  required
                  className="input mt-1.5"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Password
                <div className="relative mt-1.5">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className="input pr-11"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </label>
              <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
                {isLoading ? 'Starting your instance…' : 'Sign in'}
              </button>
            </form>
            {googleEnabled && googleClientId && (
              <div className="mt-4 space-y-3">
                <p className="text-center text-xs text-slate-400">or</p>
                <GoogleSignInButton
                  clientId={googleClientId}
                  onCredential={handleGoogle}
                  onError={setError}
                  disabled={isLoading}
                />
                <p className="text-center text-[11px] text-slate-400">
                  Use the Google account that matches your invited admin email.
                </p>
                {purchasedInstance && (
                  <p className="text-center text-[11px] leading-relaxed text-slate-400">
                    If Google shows a blank button or origin error, sign in with email and password.
                    Google must allow this host:{' '}
                    <span className="font-mono text-slate-600">{window.location.origin}</span>
                  </p>
                )}
              </div>
            )}
            <p className="mt-4 text-center text-sm text-slate-500">
              Have a welcome email?{' '}
              <a href="/set-password" className="font-medium text-teal-800 hover:underline">
                Set your password
              </a>
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="marketing-landing relative min-h-screen">
      <LandingAtmosphere className="fixed inset-0" />

      <header className="relative z-20 border-b border-teal-900/8 bg-white/50 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3.5 sm:px-6 lg:px-8">
          <a
            href="#top"
            className="flex items-center gap-2.5"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <ParrotEmblem className="h-9 w-9" animated={!reducedMotion} />
            <span className="marketing-brand-title text-xl font-bold sm:text-2xl">BirdMan</span>
            {demoSelfRegister && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900">
                Demo
              </span>
            )}
          </a>
          <nav className="hidden items-center gap-7 md:flex" aria-label="Marketing">
            {navLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => scrollToId(link.id)}
                className="text-sm font-medium text-[var(--bm-landing-muted)] transition hover:text-[var(--bm-landing-ink)]"
              >
                {link.label}
              </button>
            ))}
            {demoSelfRegister ? (
              <button
                type="button"
                onClick={() => openAuthPanel('register')}
                className="text-sm font-medium text-teal-800 hover:text-teal-950"
              >
                Try demo
              </button>
            ) : null}
            <a href="/signup" className="text-sm font-medium text-teal-800 hover:text-teal-950">
              {demoSelfRegister ? 'Get your own instance' : 'Start your BirdMan'}
            </a>
            <button
              type="button"
              onClick={() => openAuthPanel('signin')}
              className="btn btn-primary px-3.5 py-1.5 text-sm"
            >
              {demoSelfRegister ? 'Sign in' : 'Sign in'}
            </button>
          </nav>
          <div className="flex items-center gap-2 md:hidden">
            {demoSelfRegister ? (
              <button
                type="button"
                onClick={() => openAuthPanel('register')}
                className="btn btn-primary px-3 py-1.5 text-sm"
              >
                Try demo
              </button>
            ) : null}
            <a href="/signup" className="btn btn-secondary px-3 py-1.5 text-sm">
              {demoSelfRegister ? 'Own instance' : 'Start'}
            </a>
            <button
              type="button"
              onClick={() => openAuthPanel('signin')}
              className={`btn px-3.5 py-1.5 text-sm ${demoSelfRegister ? 'btn-secondary' : 'btn-primary'}`}
            >
              Sign in
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        {/* Hero: brand + headline + support + CTAs + sign-in interaction */}
        <section
          id="top"
          className="relative mx-auto grid min-h-[calc(100vh-4.25rem)] max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:gap-14 lg:px-8 lg:py-20"
        >
          <div className={`lg:col-span-7 ${reveal('marketing-reveal')}`}>
            <p className="marketing-brand-title text-5xl font-extrabold leading-none sm:text-6xl lg:text-7xl">
              BirdMan
            </p>
            <h1 className="marketing-display mt-6 max-w-xl text-2xl font-medium leading-snug text-[var(--bm-landing-ink)] sm:text-3xl">
              {headline}
            </h1>
            <p className={`mt-5 max-w-lg text-base leading-relaxed text-[var(--bm-landing-muted)] sm:text-lg ${reveal('marketing-reveal-delay-1')}`}>
              {supportCopy}
            </p>
            <div className={`mt-9 flex flex-wrap gap-3 ${reveal('marketing-reveal-delay-2')}`}>
              {demoSelfRegister ? (
                <>
                  <button
                    type="button"
                    onClick={() => openAuthPanel('register')}
                    className="btn btn-primary"
                  >
                    Create free demo account
                  </button>
                  <button
                    type="button"
                    onClick={() => openAuthPanel('signin')}
                    className="btn btn-secondary"
                  >
                    Sign in
                  </button>
                  <a href="/signup" className="btn btn-secondary">
                    Get your own instance
                  </a>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => openAuthPanel('signin')} className="btn btn-primary">
                    Sign in to your instance
                  </button>
                  <a href="/signup" className="btn btn-secondary">
                    Start your BirdMan
                  </a>
                  <button type="button" onClick={() => scrollToId('pricing')} className="btn btn-secondary">
                    View pricing
                  </button>
                </>
              )}
            </div>
            {demoSelfRegister && (
              <ul className={`mt-6 max-w-lg space-y-2 text-sm text-[var(--bm-landing-muted)] ${reveal('marketing-reveal-delay-3')}`}>
                <li className="flex gap-2">
                  <span className="text-teal-600" aria-hidden>
                    ✓
                  </span>
                  Your name, email, and password — not a shared demo login
                </li>
                <li className="flex gap-2">
                  <span className="text-teal-600" aria-hidden>
                    ✓
                  </span>
                  Viewer access to explore the full suite
                </li>
                {googleEnabled && (
                  <li className="flex gap-2">
                    <span className="text-teal-600" aria-hidden>
                      ✓
                    </span>
                    Or continue with Google
                  </li>
                )}
              </ul>
            )}
            {instance.name ? (
              <p className={`mt-8 text-sm text-[var(--bm-landing-muted)] ${reveal('marketing-reveal-delay-3')}`}>
                Connected instance{' '}
                <span className="font-semibold text-[var(--bm-landing-ink)]">{instance.name}</span>
                {instance.environment ? (
                  <span className="text-[var(--bm-landing-muted)]"> · {instance.environment}</span>
                ) : null}
              </p>
            ) : null}
          </div>

          <div id="sign-in" className={`lg:col-span-5 ${reveal('marketing-reveal-delay-2')}`}>
            <div className="scroll-mt-24 rounded-3xl border border-teal-900/10 bg-white/80 p-7 shadow-[0_28px_60px_-36px_rgba(15,118,110,0.45)] backdrop-blur-xl sm:p-8">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-400/25 to-sky-400/20 ring-1 ring-teal-700/10">
                  <ParrotEmblem
                    className={`h-8 w-8 ${reducedMotion ? '' : 'animate-[float_5s_ease-in-out_infinite]'}`}
                    animated={!reducedMotion}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="marketing-display text-xl font-semibold text-[var(--bm-landing-ink)]">
                      {demoSelfRegister ? 'Demo access' : 'Sign in'}
                    </h2>
                    {demoSelfRegister && (
                      <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-teal-900">
                        Evaluator
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--bm-landing-muted)]">
                    {demoSelfRegister
                      ? 'Create an account or sign in with your credentials'
                      : 'Access your BirdMan instance'}
                  </p>
                </div>
              </div>

              {demoSelfRegister && (
                <div className="mt-5 flex rounded-xl bg-slate-100/80 p-1">
                  <button
                    type="button"
                    className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${
                      authMode === 'signin'
                        ? 'bg-white text-teal-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => {
                      setAuthMode('signin');
                      setError('');
                    }}
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${
                      authMode === 'register'
                        ? 'bg-white text-teal-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => {
                      setAuthMode('register');
                      setError('');
                    }}
                  >
                    Create account
                  </button>
                </div>
              )}

              {error && (
                <div
                  role="alert"
                  className="mt-4 rounded-xl border border-rose-200/90 bg-rose-50/90 px-3.5 py-3 text-sm text-rose-700"
                >
                  {error}
                </div>
              )}

              {authMode === 'register' && demoSelfRegister ? (
                <form className="mt-5 space-y-4" onSubmit={handleRegister}>
                  <p className="text-xs leading-relaxed text-slate-500">
                    Evaluator accounts get <strong>viewer</strong> access — explore the full demo suite
                    but cannot manage other users. Your name, email, and last sign-in are recorded for ops.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">First name</label>
                      <input
                        className="input"
                        required
                        value={regFirstName}
                        onChange={(e) => setRegFirstName(e.target.value)}
                        autoComplete="given-name"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Last name</label>
                      <input
                        className="input"
                        required
                        value={regLastName}
                        onChange={(e) => setRegLastName(e.target.value)}
                        autoComplete="family-name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Work email</label>
                    <input
                      type="email"
                      className="input"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      placeholder="you@company.example"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
                    <div className="relative">
                      <input
                        type={showRegPassword ? 'text' : 'password'}
                        className="input pr-11"
                        required
                        minLength={6}
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400"
                        aria-label={showRegPassword ? 'Hide password' : 'Show password'}
                      >
                        {showRegPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
                    {isLoading ? 'Creating account…' : 'Create demo account'}
                  </button>
                  <p className="text-center text-xs text-slate-500">
                    Already registered?{' '}
                    <button
                      type="button"
                      className="font-semibold text-teal-800 hover:underline"
                      onClick={() => {
                        setAuthMode('signin');
                        setError('');
                      }}
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              ) : (
                <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="email-address" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Email
                    </label>
                    <input
                      id="email-address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="input appearance-none placeholder-slate-400 focus:border-teal-500 focus:ring-teal-500/25"
                      placeholder="you@aviary.example"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        className="input appearance-none pr-11 placeholder-slate-400 focus:border-teal-500 focus:ring-teal-500/25"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 transition hover:text-teal-700"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-primary relative w-full justify-center disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        {purchasedInstance ? 'Starting your instance…' : 'Signing in…'}
                      </span>
                    ) : (
                      'Sign in'
                    )}
                  </button>
                  {demoSelfRegister && (
                    <p className="text-center text-xs text-slate-500">
                      New to the demo?{' '}
                      <button
                        type="button"
                        className="font-semibold text-teal-800 hover:underline"
                        onClick={() => {
                          setAuthMode('register');
                          setError('');
                        }}
                      >
                        Create a free account
                      </button>
                    </p>
                  )}
                </form>
              )}

              {googleEnabled && googleClientId && (
                <div className="mt-4 space-y-3">
                  <div className="relative text-center text-xs text-slate-400">
                    <span className="bg-white/80 px-2">or</span>
                    <div className="absolute inset-x-0 top-1/2 -z-10 border-t border-slate-200" />
                  </div>
                  <GoogleSignInButton
                    clientId={googleClientId}
                    onCredential={handleGoogle}
                    onError={setError}
                    disabled={isLoading}
                  />
                  <p className="text-center text-[11px] leading-relaxed text-slate-400">
                    {demoSelfRegister
                      ? 'Google sign-in creates the same viewer demo account. Last sign-in is recorded.'
                      : 'Use the Google account that matches your invited admin email.'}
                  </p>
                  {purchasedInstance && (
                    <p className="text-center text-[11px] leading-relaxed text-slate-400">
                      If Google fails, use email and password. This host must be listed as an Authorized
                      JavaScript origin:{' '}
                      <span className="font-mono text-slate-600">{window.location.origin}</span>
                    </p>
                  )}
                </div>
              )}

              {demoSelfRegister && (
                <p className="mt-4 text-[11px] leading-relaxed text-slate-400">
                  Demo accounts cannot manage other users. For a dedicated production instance,{' '}
                  <a href="/signup" className="text-teal-800 hover:underline">
                    start your own BirdMan
                  </a>
                  .
                </p>
              )}
            </div>
          </div>
        </section>

        <section id="suite" className="scroll-mt-20 border-t border-teal-900/8 bg-white/55 py-20 backdrop-blur-sm">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <h2 className="marketing-display text-3xl font-semibold text-[var(--bm-landing-ink)] sm:text-4xl">
              What BirdMan covers
            </h2>
            <p className="mt-4 max-w-2xl text-[var(--bm-landing-muted)]">
              Built for aviculture operators — not a generic farm app. One purpose per area, shared flock data throughout.
            </p>
            <ul className="mt-12 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {SUITE_MODULES.map((mod) => (
                <li key={mod.title} className="border-t border-teal-800/15 pt-5">
                  <h3 className="text-base font-semibold text-[var(--bm-landing-ink)]">{mod.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--bm-landing-muted)]">{mod.body}</p>
                </li>
              ))}
            </ul>
            <ul className="mt-14 space-y-3 text-sm text-[var(--bm-landing-muted)]">
              {HOW_BIRDMAN_WORKS.map((line) => (
                <li key={line} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" aria-hidden />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <BackToTopButton />
          </div>
        </section>

        <section id="pricing" className="scroll-mt-20 py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <h2 className="marketing-display text-3xl font-semibold text-[var(--bm-landing-ink)] sm:text-4xl">
              Pricing by usage, not feature walls
            </h2>
            <p className="mt-4 max-w-2xl text-[var(--bm-landing-muted)]">
              Every plan includes the full suite. You grow into higher meters when the flock, team, or AI load needs it.
            </p>

            <div className="mt-12 grid gap-8 lg:grid-cols-3">
              {plans.map((plan) => (
                <div
                  key={plan.key}
                  className={`border-t-2 pt-5 ${
                    plan.popular ? 'border-teal-600' : 'border-teal-800/15'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="marketing-display text-xl font-semibold text-[var(--bm-landing-ink)]">
                      {plan.name}
                    </h3>
                    {plan.popular ? (
                      <span className="text-xs font-semibold uppercase tracking-wide text-teal-700">
                        Most popular
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-[var(--bm-landing-muted)]">{plan.blurb}</p>
                  <div className="mt-5">
                    <span className="marketing-display text-4xl font-bold text-[var(--bm-landing-ink)]">
                      R{plan.priceMonthlyZar}
                    </span>
                    <span className="text-sm text-[var(--bm-landing-muted)]">/mo</span>
                    <div className="mt-1 text-xs text-slate-400">≈ ${plan.priceMonthlyUsd} USD / month</div>
                  </div>
                  <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                    <li>Full product suite included</li>
                    <li>{plan.limits.birds}</li>
                    <li>{plan.limits.users}</li>
                    <li>{plan.limits.knowledgeSources}</li>
                    <li>{plan.limits.aiInvocations}</li>
                    <li>{plan.limits.storage}</li>
                    <li>{plan.limits.alwaysOn}</li>
                  </ul>
                </div>
              ))}
            </div>

            <p className="mt-10 text-sm text-[var(--bm-landing-muted)]">
              Annual billing (when configured): two months free (~17% off). Optional onboarding / migration: USD 149.
              Always-on Neon compute for Business is an ops choice when background polling must stay warm — not a hidden feature gate.
            </p>
            <BackToTopButton />
          </div>
        </section>

        <section id="subscribe" className="scroll-mt-20 border-t border-teal-900/8 bg-white/55 py-20 backdrop-blur-sm">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <h2 className="marketing-display text-3xl font-semibold text-[var(--bm-landing-ink)] sm:text-4xl">
              Subscription & contracting
            </h2>
            <p className="mt-4 max-w-2xl text-[var(--bm-landing-muted)]">
              Purchase and renewal run through Stripe when billing is enabled on the instance. Self-serve Starter and Professional;
              Business / multi-location may involve light-touch onboarding with your operator.
            </p>

            <ol className="mt-12 grid gap-8 sm:grid-cols-2">
              {SUBSCRIBE_STEPS.map((step, index) => (
                <li key={step.title} className="flex gap-4">
                  <span className="marketing-display flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-700 text-sm font-bold text-white">
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-[var(--bm-landing-ink)]">{step.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-[var(--bm-landing-muted)]">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>

            <h3 className="marketing-display mt-14 text-xl font-semibold text-[var(--bm-landing-ink)]">
              Contracts & compliance
            </h3>
            <ul className="mt-5 space-y-3 text-sm text-[var(--bm-landing-muted)]">
              {CONTRACTING_NOTES.map((note) => (
                <li key={note} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-600" aria-hidden />
                  <span>{note}</span>
                </li>
              ))}
            </ul>

            {legalLinks.length > 0 ? (
              <div className="mt-8 flex flex-wrap gap-5 text-sm">
                {legalLinks.map((link) => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="brand-link font-semibold"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            ) : (
              <p className="mt-8 text-sm text-[var(--bm-landing-muted)]">
                Published Terms and Privacy URLs are not configured on this build yet (
                <code className="text-xs">VITE_TERMS_URL</code> / <code className="text-xs">VITE_PRIVACY_URL</code>
                ). Templates live in the BirdMan <code className="text-xs">/legal</code> folder for operator review.
              </p>
            )}
            <BackToTopButton />
          </div>
        </section>

        <section id="updates" className="scroll-mt-20 py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <h2 className="marketing-display text-3xl font-semibold text-[var(--bm-landing-ink)] sm:text-4xl">
              Updates & improvements
            </h2>
            <p className="mt-4 max-w-2xl text-[var(--bm-landing-muted)]">
              How you hear about changes — and what stays honest as the product evolves.
            </p>
            <div className="mt-12 grid gap-10 sm:grid-cols-2">
              {UPDATES_AND_IMPROVEMENTS.map((item) => (
                <div key={item.title} className="border-l-2 border-teal-600/50 pl-5">
                  <h3 className="font-semibold text-[var(--bm-landing-ink)]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--bm-landing-muted)]">{item.body}</p>
                </div>
              ))}
            </div>
            <BackToTopButton />
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-teal-900/8 bg-white/70 py-10 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            <ParrotEmblem className="h-7 w-7" animated={false} />
            <span className="marketing-brand-title text-lg font-bold">BirdMan</span>
            <span className="text-sm text-[var(--bm-landing-muted)]">· aviary operations suite</span>
          </div>
          <div className="flex flex-wrap items-center gap-5 text-sm text-[var(--bm-landing-muted)]">
            {demoSelfRegister ? (
              <button
                type="button"
                onClick={() => openAuthPanel('register')}
                className="transition hover:text-[var(--bm-landing-ink)]"
              >
                Try demo
              </button>
            ) : null}
            <button type="button" onClick={() => openAuthPanel('signin')} className="transition hover:text-[var(--bm-landing-ink)]">
              Sign in
            </button>
            <a href="/signup" className="transition hover:text-[var(--bm-landing-ink)]">
              {demoSelfRegister ? 'Own instance' : 'Start BirdMan'}
            </a>
            <button type="button" onClick={() => scrollToId('pricing')} className="transition hover:text-[var(--bm-landing-ink)]">
              Pricing
            </button>
            <button type="button" onClick={() => scrollToId('top')} className="transition hover:text-[var(--bm-landing-ink)]">
              Back to top
            </button>
            {legalLinks.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-[var(--bm-landing-ink)]"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
